// src/pages/Catalogue.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  listActiveProducts,
  listPublicBrands,
  listPublicCategories,
} from "../services/publicProductService";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";

/* ---------- Utils : normalisation ---------- */
function normalize(str = "") {
  return String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s.'-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/* ---------- URL pretty (q=robe-rouge-m) ---------- */
const encodePrettyQ = (raw = "") => {
  const s = String(raw || "").trim();
  if (!s) return "";
  // "robe rouge m" -> "robe-rouge-m"
  return s.replace(/\s+/g, "-").replace(/-+/g, "-");
};
const decodePrettyQ = (q = "") => {
  const s = String(q || "").trim();
  if (!s) return "";
  // "robe-rouge-m" -> "robe rouge m"
  return s.replace(/-+/g, " ");
};

const COLORS = [
  "noir", "blanc", "gris", "rouge", "bleu", "vert", "jaune", "marron", "beige",
  "rose", "orange", "violet", "kaki", "bordeaux", "marine", "camel", "taupe", "ivoire",
];
const SIZES_TEXT = ["xs", "s", "m", "l", "xl", "xxl", "xxxl"];
const SIZE_NUM_RANGE = { min: 30, max: 54 };
const SHOES_RANGE = { min: 35, max: 47 };

function parseSmartQuery(raw = "") {
  const q = normalize(raw);
  const tokens = q.split(" ").filter(Boolean);
  const rest = [];

  let color = "";
  let size = "";
  let priceMin = "";
  let priceMax = "";

  for (const t of tokens) {
    if (COLORS.includes(t)) { color = t; continue; }
    if (SIZES_TEXT.includes(t)) { size = t.toUpperCase(); continue; }

    if (/^\d{2}$/.test(t)) {
      const n = Number(t);
      if (!size && n >= SHOES_RANGE.min && n <= SHOES_RANGE.max) { size = String(n); continue; }
      if (!size && n >= SIZE_NUM_RANGE.min && n <= SIZE_NUM_RANGE.max) { size = String(n); continue; }
    }

    if (/^(<=|>=|<|>)\d{3,}$/.test(t)) {
      const m = t.match(/^(<=|>=|<|>)(\d+)$/);
      if (m) {
        const op = m[1];
        const num = m[2];
        if (op === "<" || op === "<=") priceMax = num;
        if (op === ">" || op === ">=") priceMin = num;
        continue;
      }
    }

    if (/^\d{1,3}k$/.test(t)) {
      const n = parseInt(t, 10) * 1000;
      if (!priceMin) priceMin = String(n);
      else priceMax = String(n);
      continue;
    }

    rest.push(t);
  }

  const search = rest.join(" ");
  return { search, color, size, priceMin, priceMax };
}

/* ---------- Stock : NE JAMAIS afficher hors stock ---------- */
const isInStockProduct = (p) => {
  const variants = Array.isArray(p?.variants) ? p.variants : [];
  if (variants.length > 0) return variants.some((v) => Number(v?.stock || 0) > 0);
  return Number(p?.stock || 0) > 0;
};

/* ---------- Filtre FRONT (secours si Firestore search n’est pas OK) ---------- */
function clientFilterProducts(items, { qText, size, minPrice, maxPrice, onlySale }) {
  const qn = normalize(qText);
  const { search, color, size: smartSize, priceMin, priceMax } = parseSmartQuery(qText);

  const min = Number(minPrice || priceMin || 0);
  const max = Number(maxPrice || priceMax || 0);

  const wantedSize = (size || smartSize || "").toString().toUpperCase();
  const wantedColor = (color || "").toString().toLowerCase();
  const wantedSearch = normalize(search);

  const hasSale = (p) => {
    // adapte selon ton modèle, je laisse simple :
    const promo = Number(p?.priceTextPromo || p?.salePrice || 0);
    const normal = Number(p?.priceText || p?.price || 0);
    return promo > 0 && promo < normal;
  };

  return (items || [])
    .filter(isInStockProduct)
    .filter((p) => {
      // promo
      if (onlySale === "1" && !hasSale(p)) return false;

      // prix
      const price = Number(p?.price || 0);
      if (Number.isFinite(min) && min > 0 && price < min) return false;
      if (Number.isFinite(max) && max > 0 && price > max) return false;

      // taille (si variantes)
      if (wantedSize) {
        const variants = Array.isArray(p?.variants) ? p.variants : [];
        if (variants.length > 0) {
          const ok = variants.some((v) => {
            const label = normalize(v?.label || v?.size || v?.name || "");
            const key = normalize(v?.key || "");
            return label.includes(normalize(wantedSize)) || key.includes(normalize(wantedSize));
          });
          if (!ok) return false;
        }
      }

      // couleur (si champ couleur ou variantes)
      if (wantedColor) {
        const colorField = normalize(p?.color || p?.couleur || "");
        const variants = Array.isArray(p?.variants) ? p.variants : [];
        const ok =
          colorField.includes(wantedColor) ||
          variants.some((v) => normalize(v?.color || v?.couleur || v?.label || "").includes(wantedColor));
        if (!ok) return false;
      }

      // recherche texte
      if (wantedSearch || qn) {
        const hay = normalize(
          [
            p?.title, p?.name, p?.shortName,
            p?.description, p?.categoryName, p?.brandName,
            p?.code,
          ].filter(Boolean).join(" ")
        );
        const needle = wantedSearch || qn;
        if (!hay.includes(needle)) return false;
      }

      return true;
    });
}

/* ---------- Mobile sheet overlay ---------- */
function MobileFiltersSheet({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="mf-sheet" role="dialog" aria-modal="true" aria-label="Filtres">
      <div className="mf-backdrop" onClick={onClose} />
      <div className="mf-panel">
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <strong>Filtres</strong>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
        <div className="p-3 mf-body">{children}</div>
      </div>

      <style>{`
        .mf-sheet { position: fixed; inset: 0; z-index: 1055; }
        .mf-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.35); }
        .mf-panel { position: absolute; right: 0; top: 0; bottom: 0; width: min(92vw, 420px); background: #fff; box-shadow: -12px 0 24px rgba(0,0,0,.12); display:flex; flex-direction:column; }
        .mf-body { overflow:auto; height: 100%; }
      `}</style>
    </div>
  );
}

/* ---------- Panneau de filtres ---------- */
const SIZES_TEXT_UI = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const SIZE_NUM_UI = Array.from({ length: 54 - 30 + 1 }, (_, i) => String(30 + i));
const SHOES_UI = Array.from({ length: 47 - 35 + 1 }, (_, i) => String(35 + i));

function FiltersPanel({
  q,
  brandId,
  categoryId,
  sort,
  size,
  minPrice,
  maxPrice,
  onlySale,
  brands,
  categories,
  onFilter,
  onClearAll,
  onSmartSearch,
}) {
  const [localQ, setLocalQ] = useState(q || "");
  useEffect(() => setLocalQ(q || ""), [q]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (localQ !== q) onFilter("q", localQ);
    }, 280);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQ]);

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="m-0">Filtres</h6>
          <button className="btn btn-sm btn-link text-danger" onClick={onClearAll}>
            Effacer tout
          </button>
        </div>

        {/* Recherche */}
        <div className="mb-3">
          <label className="form-label">Recherche</label>
          <div className="input-group">
            <input
              className="form-control"
              type="search"
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              placeholder="Ex: robe rouge M, chemise <20000, basket 42"
            />
            <button
              type="button"
              className="btn btn-dark"
              onClick={() => onSmartSearch(localQ)}
              title="Recherche intelligente"
            >
              OK
            </button>
          </div>
          <div className="small text-muted mt-1">
            Astuce : <strong>&lt;20000</strong>, <strong>&gt;15000</strong>, <strong>20k</strong>, <strong>M</strong>, <strong>42</strong>, couleur…
          </div>
        </div>

        {/* Catégorie */}
        <div className="mb-3">
          <label className="form-label">Catégorie</label>
          <select className="form-select" value={categoryId} onChange={(e) => onFilter("categoryId", e.target.value)}>
            <option value="">Toutes</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Marque */}
        <div className="mb-3">
          <label className="form-label">Marque</label>
          <select className="form-select" value={brandId} onChange={(e) => onFilter("brandId", e.target.value)}>
            <option value="">Toutes</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {/* Taille */}
        <div className="mb-3">
          <label className="form-label">Taille</label>
          <input
            className="form-control"
            list="sizesList"
            placeholder="Ex: M, 42, 46…"
            value={size}
            onChange={(e) => onFilter("size", e.target.value.toUpperCase())}
          />
          <datalist id="sizesList">
            {SIZES_TEXT_UI.map((s) => <option key={s} value={s} />)}
            {SIZE_NUM_UI.map((s) => <option key={`t-${s}`} value={s} />)}
            {SHOES_UI.map((s) => <option key={`p-${s}`} value={s} />)}
          </datalist>
        </div>

        {/* Prix */}
        <div className="mb-3">
          <label className="form-label">Prix (FCFA)</label>
          <div className="d-flex gap-2">
            <input type="number" min={0} className="form-control" placeholder="Min" value={minPrice} onChange={(e) => onFilter("minPrice", e.target.value)} />
            <input type="number" min={0} className="form-control" placeholder="Max" value={maxPrice} onChange={(e) => onFilter("maxPrice", e.target.value)} />
          </div>
        </div>

        {/* Promo */}
        <div className="mb-3">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="onlySale"
              checked={onlySale === "1"}
              onChange={(e) => onFilter("onlySale", e.target.checked ? "1" : "")}
            />
            <label className="form-check-label" htmlFor="onlySale">En promo uniquement</label>
          </div>
          <div className="small text-muted mt-1">✅ Les produits hors stock sont automatiquement masqués.</div>
        </div>

        {/* Tri */}
        <div className="mb-0">
          <label className="form-label">Trier par</label>
          <select className="form-select" value={sort} onChange={(e) => onFilter("sort", e.target.value)}>
            <option value="new">Nouveautés</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
          </select>
        </div>
      </div>
    </div>
  );
}

/* ---------- Skeleton ---------- */
function ProductSkeleton() {
  return (
    <div className="card h-100 shadow-sm">
      <div className="placeholder-glow">
        <div className="card-img-top bg-light" style={{ height: "clamp(130px, 28vw, 210px)" }} />
        <div className="card-body">
          <span className="placeholder col-7" />
          <div className="mt-2"><span className="placeholder col-9" /></div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="placeholder col-4" />
            <span className="placeholder col-3" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------- Page Catalogue ---------------------- */
export default function Catalogue() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [params, setParams] = useSearchParams();
  const { addItem } = useCart();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ✅ q = nouveau param (beau) + fallback search (ancien)
  const qParam = params.get("q") || "";
  const legacySearch = params.get("search") || "";
  const q = qParam ? decodePrettyQ(qParam) : legacySearch;

  const brandId = params.get("brandId") || "";
  const categoryId = params.get("categoryId") || "";
  const size = (params.get("size") || "").toUpperCase();
  const minPrice = params.get("minPrice") || "";
  const maxPrice = params.get("maxPrice") || "";
  const onlySale = params.get("onlySale") || "";
  const sort = params.get("sort") || "new";

  // Force onlyInStock=1 TOUJOURS
  useEffect(() => {
    const draft = new URLSearchParams(params);
    if (draft.get("onlyInStock") !== "1") {
      draft.set("onlyInStock", "1");
      setParams(draft, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ migration automatique : si search= existe, on convertit en q= joli
  useEffect(() => {
    if (!legacySearch || qParam) return;
    const draft = new URLSearchParams(params);
    draft.delete("search");
    draft.set("q", encodePrettyQ(legacySearch));
    setParams(draft, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacySearch, qParam]);

  const activeFiltersCount = useMemo(() => {
    return [q, brandId, categoryId, size, minPrice, maxPrice, onlySale].filter(Boolean).length;
  }, [q, brandId, categoryId, size, minPrice, maxPrice, onlySale]);

  const brandLabel = useMemo(
    () => brands.find((b) => String(b.id) === String(brandId))?.name || "",
    [brands, brandId]
  );
  const categoryLabel = useMemo(
    () => categories.find((c) => String(c.id) === String(categoryId))?.name || "",
    [categories, categoryId]
  );

  // Load meta
  useEffect(() => {
    (async () => {
      const [b, c] = await Promise.all([listPublicBrands(), listPublicCategories()]);
      setBrands(Array.isArray(b) ? b : []);
      setCategories(Array.isArray(c) ? c : []);
    })();
  }, []);

  // Charger produits (on garde ton backend) + FILTRE FRONT en secours
  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listActiveProducts({
          // ✅ on envoie q “lisible”
          search: q,
          brandId,
          categoryId,
          sort,
          size,
          minPrice,
          maxPrice,
          onlySale,
          onlyInStock: "1",
          pageSize: 12,
          cursor: null,
        });

        const raw = Array.isArray(res?.items) ? res.items : [];
        const safe = clientFilterProducts(raw, { qText: q, size, minPrice, maxPrice, onlySale });

        if (!aborted) {
          setItems(safe);
          setCursor(res?.nextCursor || null);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [q, brandId, categoryId, sort, size, minPrice, maxPrice, onlySale]);

  // Lazy load
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && cursor && !loading && !loadingMore) loadMore();
      },
      { rootMargin: "600px 0px 600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, loading, loadingMore]);

  const loadMore = async () => {
    if (!cursor || loading || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await listActiveProducts({
        search: q,
        brandId,
        categoryId,
        sort,
        size,
        minPrice,
        maxPrice,
        onlySale,
        onlyInStock: "1",
        pageSize: 12,
        cursor,
      });

      const raw = Array.isArray(res?.items) ? res.items : [];
      const safe = clientFilterProducts(raw, { qText: q, size, minPrice, maxPrice, onlySale });

      setItems((prev) => {
        const seen = new Set(prev.map((x) => x?.id || x?.slug));
        const merged = [...prev];
        for (const p of safe) {
          const k = p?.id || p?.slug;
          if (!k || seen.has(k)) continue;
          seen.add(k);
          merged.push(p);
        }
        return merged;
      });

      setCursor(res?.nextCursor || null);
    } finally {
      setLoadingMore(false);
    }
  };

  // URL filter helper
  const onFilter = useCallback((k, v) => {
    const draft = new URLSearchParams(params);

    draft.set("onlyInStock", "1");
    if (!draft.get("sort")) draft.set("sort", "new");

    if (k === "q") {
      // ✅ q joli
      const val = encodePrettyQ(v);
      if (val) draft.set("q", val);
      else draft.delete("q");
      draft.delete("search"); // legacy out
    } else {
      if (v) draft.set(k, v);
      else draft.delete(k);
    }

    draft.delete("cursor");
    setParams(draft);
  }, [params, setParams]);

  const clearAll = useCallback(() => {
    const draft = new URLSearchParams();
    draft.set("onlyInStock", "1");
    draft.set("sort", "new");
    setParams(draft);
  }, [setParams]);

  // Smart search action (bouton OK)
  const onSmartSearch = useCallback((raw) => {
    const { color, size: smartSize, priceMin, priceMax } = parseSmartQuery(raw);

    const draft = new URLSearchParams(params);
    draft.set("onlyInStock", "1");
    draft.set("sort", sort || "new");

    // q joli = texte tapé (pas seulement "search")
    const qPretty = encodePrettyQ(raw);
    if (qPretty) draft.set("q", qPretty);
    else draft.delete("q");
    draft.delete("search");

    // auto-complete filtres issus du smart search
    if (smartSize) draft.set("size", smartSize);
    if (priceMin) draft.set("minPrice", priceMin);
    if (priceMax) draft.set("maxPrice", priceMax);
    // (couleur : tu peux stocker en param si tu veux l’afficher en chip)
    if (color) draft.set("color", color);

    draft.delete("cursor");
    setParams(draft);
  }, [params, setParams, sort]);

  // Chips
  const activeChips = useMemo(() => {
    const chips = [];
    const push = (key, label) => chips.push({ key, label });

    if (q) push("q", `“${q}”`);
    if (brandLabel) push("brandId", brandLabel);
    if (categoryLabel) push("categoryId", categoryLabel);
    if (size) push("size", size);
    if (minPrice) push("minPrice", `≥ ${Number(minPrice).toLocaleString("fr-FR")} FCFA`);
    if (maxPrice) push("maxPrice", `≤ ${Number(maxPrice).toLocaleString("fr-FR")} FCFA`);
    if (onlySale === "1") push("onlySale", "Promo");

    // chip optionnelle si tu utilises color=...
    const color = params.get("color") || "";
    if (color) push("color", `Couleur: ${color}`);

    return chips;
  }, [q, brandLabel, categoryLabel, size, minPrice, maxPrice, onlySale, params]);

  const visibleItems = useMemo(() => items.filter(isInStockProduct), [items]);

  return (
    <div className="container py-4">
      {/* Toolbar mobile */}
      <div className="d-lg-none d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary position-relative"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            {filtersOpen ? "Fermer" : "Filtres"}
            {activeFiltersCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill text-bg-primary">
                {activeFiltersCount}
              </span>
            )}
          </button>

          <select
            className="form-select"
            style={{ width: 160 }}
            value={sort}
            onChange={(e) => onFilter("sort", e.target.value)}
          >
            <option value="new">Nouveautés</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
          </select>
        </div>

        <div className="small text-muted" aria-live="polite">
          {visibleItems.length} résultat{visibleItems.length > 1 ? "s" : ""}
        </div>
      </div>

      {/* Chips */}
      {activeChips.length > 0 && (
        <div className="mb-3 d-flex flex-wrap gap-2">
          {activeChips.map((c) => (
            <button
              type="button"
              key={c.key}
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onFilter(c.key, "")}
              title={`Retirer filtre ${c.label}`}
            >
              {c.label} <span className="ms-1">×</span>
            </button>
          ))}
          <button type="button" className="btn btn-sm btn-link text-danger" onClick={clearAll}>
            Effacer tout
          </button>
        </div>
      )}

      {/* Sheet mobile */}
      <MobileFiltersSheet open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <FiltersPanel
          q={q}
          brandId={brandId}
          categoryId={categoryId}
          sort={sort}
          size={size}
          minPrice={minPrice}
          maxPrice={maxPrice}
          onlySale={onlySale}
          brands={brands}
          categories={categories}
          onFilter={onFilter}
          onClearAll={clearAll}
          onSmartSearch={(v) => { onSmartSearch(v); setFiltersOpen(false); }}
        />
      </MobileFiltersSheet>

      <div className="row g-4">
        {/* Filtres desktop */}
        <div className="d-none d-lg-block col-lg-3">
          <div className="filters-sticky">
            <FiltersPanel
              q={q}
              brandId={brandId}
              categoryId={categoryId}
              sort={sort}
              size={size}
              minPrice={minPrice}
              maxPrice={maxPrice}
              onlySale={onlySale}
              brands={brands}
              categories={categories}
              onFilter={onFilter}
              onClearAll={clearAll}
              onSmartSearch={onSmartSearch}
            />
          </div>

          <style>{`
    .filters-sticky{
      position: sticky;
      top: 88px;              /* ajuste selon ta navbar */
      max-height: calc(100vh - 100px);
      overflow: auto;         /* scroll interne si beaucoup de filtres */
      padding-bottom: 8px;
    }
  `}</style>
        </div>


        {/* Liste */}
        <div className="col-12 col-lg-9">
          {/* Header desktop sticky */}
          <div className="d-none d-lg-flex justify-content-between align-items-center mb-2 position-sticky top-0 bg-white py-2" style={{ zIndex: 1 }}>
            <div className="small text-muted">
              {visibleItems.length} résultat{visibleItems.length > 1 ? "s" : ""} • <strong>En stock uniquement</strong>
              {brandLabel && <> • <strong>{brandLabel}</strong></>}
              {categoryLabel && <> • <strong>{categoryLabel}</strong></>}
              {size && <> • <strong>{size}</strong></>}
              {onlySale === "1" && <> • <strong>Promo</strong></>}
            </div>

            <div className="d-flex align-items-center gap-2">
              <label className="small text-muted">Tri</label>
              <select className="form-select form-select-sm" value={sort} onChange={(e) => onFilter("sort", e.target.value)}>
                <option value="new">Nouveautés</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
              </select>
            </div>
          </div>

          {/* Contenu */}
          {loading && visibleItems.length === 0 ? (
            <div className="row g-3 mb-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={`sk-${i}`} className="col-6 col-sm-6 col-md-4 col-xl-3 d-flex">
                  <div className="w-100"><ProductSkeleton /></div>
                </div>
              ))}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="alert alert-light">
              <strong>Aucun article en stock</strong> avec ces filtres.
              <hr />
              <div className="small text-muted">
                Essaye : <em>robe</em>, <em>chemise</em>, <em>jean</em>, <em>basket 42</em>, <em>&lt;20000</em>…
              </div>
              <div className="mt-2 d-flex flex-wrap gap-2">
                <button className="btn btn-primary btn-sm" onClick={clearAll}>Tout réinitialiser</button>
              </div>
            </div>
          ) : (
            <>
              <div className="row g-3 mb-3">
                {visibleItems.map((p) => (
                  <div key={p.id || p.slug} className="col-6 col-sm-6 col-md-4 col-xl-3 d-flex">
                    <div className="w-100">
                      <ProductCard
                        p={p}
                        onAdd={(product, _delta, { variantKey, variant, variantLabel }) => {
                          addItem({
                            productId: product.id || product.slug,
                            variantKey: variantKey || "default",
                            variant: variant || null,
                            variantLabel: variantLabel || undefined,
                            title: product.title,
                            price: Number(product.price || 0),
                            image: product.images?.[0] || "",
                            qty: 1,
                            maxQty: Number(variant?.stock ?? product?.stock ?? 0),
                          });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div ref={sentinelRef} />
              <div className="d-grid" style={{ marginBottom: "80px" }}>
                <button
                  className="btn btn-outline-primary mx-auto"
                  onClick={loadMore}
                  disabled={!cursor || loading || loadingMore}
                  style={{ width: "145px" }}
                >
                  {loadingMore ? "Chargement…" : cursor ? "Charger plus" : "Fin de liste"}
                </button>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}
